import CRC32 from 'crc-32';
import {
  S3Client,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3';
import { message } from 'antd';

export const uploadFileWithCRC32 = async ({ file, bucket, key, s3Config, onProgress }) => {
  if (!file || typeof file.size !== 'number' || typeof file.slice !== 'function') {
    throw new Error('Invalid or missing "file" parameter. Expected a Blob/File object.');
  }
  if (!bucket || typeof bucket !== 'string') {
    throw new Error('Invalid or missing "bucket" parameter. Expected a string.');
  }
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid or missing "key" parameter. Expected a string.');
  }
  if (!s3Config || typeof s3Config !== 'object') {
    throw new Error('Invalid or missing "s3Config" parameter. Expected an object.');
  }

  const s3 = new S3Client(s3Config);
  const partSize = 5 * 1024 * 1024;
  const totalParts = Math.ceil(file.size / partSize);

  let uploadId;
  const parts = [];
  let loaded = 0;
  try {
    const createRes = await s3.send(
      new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        ChecksumAlgorithm: 'CRC32',
      }),
    );
    uploadId = createRes.UploadId;

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);

      const arrayBuffer = await blob.arrayBuffer();
      const crc32Value = CRC32.buf(new Uint8Array(arrayBuffer)) >>> 0;
      const crc32Base64 = btoa(
        String.fromCharCode(
          (crc32Value >>> 24) & 0xff,
          (crc32Value >>> 16) & 0xff,
          (crc32Value >>> 8) & 0xff,
          crc32Value & 0xff,
        ),
      );

      const uploadPartRes = await s3.send(
        new UploadPartCommand({
          Bucket: bucket,
          Key: key,
          PartNumber: partNumber,
          UploadId: uploadId,
          Body: blob,
          ChecksumCRC32: crc32Base64,
        }),
      );

      loaded += end - start;
      if (onProgress) {
        onProgress({ loaded, total: file.size, partNumber });
      }

      parts.push({
        ETag: uploadPartRes.ETag,
        PartNumber: partNumber,
        ChecksumCRC32: crc32Base64,
      });
    }

    await s3.send(
      new CompleteMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: { Parts: parts },
      }),
    );
  } catch (err) {
    if (uploadId) {
      try {
        await s3.send(
          new AbortMultipartUploadCommand({
            Bucket: bucket,
            Key: key,
            UploadId: uploadId,
          }),
        );
      } catch (abortErr) {
        message.error(abortErr.message);
      }
    }
    throw err;
  }
};
